import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { VideoData } from '@lumen5/luminary';

import { SourceEnum } from 'js/src/client';
import type { GenerativeMediaModels, ModelNameEnum, ModelTypeEnum } from 'js/src/client/types.gen';
import { ModelNameEnum as ModelNames } from 'js/src/client/types.gen';
import Button, { SecondaryButton } from 'js/src/components/common/ui/Button';
import { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { SupportedFileType } from 'js/src/components/sketch/FileUpload';
import { getCanUseShutterstockUnlimitedMedia, getIsUserStaff } from 'js/src/data/account';
import {
    EDITOR_TAB_OPTIONS,
    setAdvancedMediaControlsState,
    switchEditorTab,
} from 'js/src/data/app';
import {
    AI_MEDIA_SUB_TAB_OPTIONS,
    changeActiveAIMediaSubTab,
    getAIMediaModels,
    getUserSelectedGenMediaAspectRatio,
    setSelectedReferenceImage,
    setSelectedReferenceVideo,
    setUserSelectedGenMediaAspectRatio,
} from 'js/src/data/media';
import { hideModal, LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { getUnusedCreditCount } from 'js/src/data/premium-media/premium-media-selectors';
import { getEnumKeyByEnumValue } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { useAppDispatch } from 'js/src/libs/RootState';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';

export const useVideoGenerationSettings = (modelType: ModelTypeEnum) => {
    const dispatch = useAppDispatch();
    const [selectedModelName, setSelectedModelName] = useState<ModelNameEnum | null>(null);
    const [selectedAspectRatio, setSelectedAspectRatio] = useState<string | null>(null);
    const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
    const videoAspectRatio = useLuminarySelector(
        (luminary) => VideoData.getAspectRatioDisplayRatio(luminary.aspectRatio),
        [],
    );
    const userSelectedAspectRatio = useSelector(getUserSelectedGenMediaAspectRatio);
    const isStaff = useSelector(getIsUserStaff);

    const allModels = useSelector(getAIMediaModels);

    const models = allModels.filter(
        (m) => m.model_type === modelType && (m.is_visible_to_public || isStaff),
    );

    const currentModel: GenerativeMediaModels | null =
        models.find((m) => m.model_name === selectedModelName) ?? null;
    const supportedAspectRatios = currentModel?.supported_aspect_ratios ?? [];
    const supportedDurations = currentModel?.supported_duration_seconds ?? [];

    const expectedCost = useMemo(() => {
        if (!currentModel || !selectedDuration) {
            return;
        }
        // Video generation is billed in animation minutes: cost equals the generated
        // duration in seconds, independent of model.
        return selectedDuration;
    }, [currentModel, selectedDuration]);

    const setSelectedModel = useCallback(
        (modelName: ModelNameEnum | null) => {
            setSelectedModelName(modelName);
            const model = models.find((m) => m.model_name === modelName);
            if (model) {
                setSelectedAspectRatio(model.supported_aspect_ratios[0]?.internal_value ?? null);
                setSelectedDuration(model.supported_duration_seconds[0]?.internal_value ?? null);
            } else {
                setSelectedAspectRatio(null);
                setSelectedDuration(null);
            }
        },
        [models],
    );

    const setAspectRatio = useCallback(
        (value: string | null) => {
            setSelectedAspectRatio(value);
            dispatch(setUserSelectedGenMediaAspectRatio(value));
        },
        [dispatch],
    );

    const setDuration = useCallback((value: number | null) => {
        setSelectedDuration(value);
    }, []);

    useEffect(() => {
        if (models.length > 0) {
            const currentSelectedModel = models.find((m) => m.model_name === selectedModelName);
            if (!currentSelectedModel) {
                // Prefer Gemini Omni Flash as default, fall back to first model
                const preferredModelName =
                    modelType === 'text_to_video'
                        ? ModelNames.GOOGLE_GEMINI_OMNI_FLASH
                        : ModelNames.GOOGLE_GEMINI_OMNI_FLASH_REFERENCE_TO_VIDEO;
                const firstModel =
                    models.find((m) => m.model_name === preferredModelName) ?? models[0];

                // Priority order for default aspect ratio:
                // 1. User's previously selected aspect ratio (if supported by model)
                // 2. Current video's aspect ratio (if supported by model)
                // 3. Fallback for similar aspect ratios (e.g., portrait -> square)
                // 4. Model's first supported aspect ratio
                const findSupportedAspectRatio = (aspectRatio: string | null) =>
                    aspectRatio &&
                    firstModel.supported_aspect_ratios.find(
                        (ar) => ar.internal_value === aspectRatio,
                    );

                let defaultAspectRatio: string | null = null;
                if (findSupportedAspectRatio(userSelectedAspectRatio)) {
                    defaultAspectRatio = userSelectedAspectRatio;
                } else if (findSupportedAspectRatio(videoAspectRatio)) {
                    defaultAspectRatio = videoAspectRatio;
                } else if (videoAspectRatio === '4:5' && findSupportedAspectRatio('1:1')) {
                    // Portrait (4:5) falls back to square (1:1) since they're similar
                    defaultAspectRatio = '1:1';
                } else {
                    defaultAspectRatio =
                        firstModel.supported_aspect_ratios[0]?.internal_value ?? null;
                }

                setSelectedModelName(firstModel.model_name);
                setSelectedAspectRatio(defaultAspectRatio);
                setSelectedDuration(
                    firstModel.supported_duration_seconds[0]?.internal_value ?? null,
                );
            }
        }
    }, [models, modelType, selectedModelName, userSelectedAspectRatio, videoAspectRatio]);

    return {
        models,
        selectedModelName,
        selectedAspectRatio,
        selectedDuration,
        currentModel,
        supportedAspectRatios,
        supportedDurations,
        expectedCost,
        setSelectedModel,
        setAspectRatio,
        setDuration,
    };
};

export const useImageGenerationSettings = (modelType: ModelTypeEnum) => {
    const [selectedModelName, setSelectedModelName] = useState<ModelNameEnum | null>(null);
    const isStaff = useSelector(getIsUserStaff);

    const allModels = useSelector(getAIMediaModels);

    const models = allModels.filter(
        (m) => m.model_type === modelType && (m.is_visible_to_public || isStaff),
    );

    const currentModel: GenerativeMediaModels | null =
        models.find((m) => m.model_name === selectedModelName) ?? null;

    const setSelectedModel = useCallback((modelName: ModelNameEnum | null) => {
        setSelectedModelName(modelName);
    }, []);

    useEffect(() => {
        if (models.length > 0) {
            const currentSelectedModel = models.find((m) => m.model_name === selectedModelName);
            if (!currentSelectedModel) {
                const firstModel = models[0];
                setSelectedModelName(firstModel.model_name);
            }
        }
    }, [models, selectedModelName]);

    return {
        models,
        selectedModelName,
        currentModel,
        setSelectedModel,
    };
};

export type TransformImageData = {
    imageUrl: string;
    uuid: string;
    name: string;
};

type TransformImageAnalyticsMeta = {
    eventName: string;
    location: string;
    extraProps?: Record<string, unknown>;
};

type UseTransformImageParams = {
    transformImageData: TransformImageData | null;
    assetSource: SourceEnum;
    analyticsMeta?: TransformImageAnalyticsMeta;
};

export const useTransformImage = ({
    transformImageData,
    assetSource,
    analyticsMeta,
}: UseTransformImageParams) => {
    const dispatch = useAppDispatch();
    const canUseShutterstockUnlimitedMedia = useSelector(getCanUseShutterstockUnlimitedMedia);
    const unusedCreditCount = useSelector(getUnusedCreditCount);

    const handleTransformImage = useCallback(
        (aiMediaOption: AI_MEDIA_SUB_TAB_OPTIONS) => {
            if (!transformImageData) return;

            const uploadedFile: UploadedFile = {
                id: transformImageData.uuid,
                uuid: transformImageData.uuid,
                name: transformImageData.name,
                type: SupportedFileType.WEBP,
                isUploading: false,
                local_preview_url: transformImageData.imageUrl,
            };

            const source = getEnumKeyByEnumValue(SourceEnum, assetSource);
            dispatch(setAdvancedMediaControlsState({ isVisible: false, sceneId: '', assetId: '' }));
            dispatch(changeActiveAIMediaSubTab(aiMediaOption));
            dispatch(setSelectedReferenceImage(uploadedFile));
            dispatch(switchEditorTab(EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA));
            if (analyticsMeta) {
                analytics.track(analyticsMeta.eventName, {
                    location: analyticsMeta.location,
                    original_media_source: source,
                    action: aiMediaOption,
                    ...analyticsMeta.extraProps,
                });
            }
        },
        [analyticsMeta, assetSource, dispatch, transformImageData],
    );

    const onTransformImage = useCallback(
        (aiMediaOption: AI_MEDIA_SUB_TAB_OPTIONS) => {
            if (!transformImageData) return;

            if (
                [SourceEnum.SOURCE_DAM, SourceEnum.SOURCE_SHUTTERSTOCK_EDITORIAL].includes(
                    assetSource,
                )
            ) {
                ErrorReporter.capture(
                    `User tried to transform DAM or editorial media: ${assetSource}`,
                );
                return;
            }

            const isPremiumAsset = assetSource === SourceEnum.SOURCE_SHUTTERSTOCK_CHOICES;
            const isMarketplaceAsset = assetSource === SourceEnum.SOURCE_SHUTTERSTOCK_PREMIUM;
            if (isPremiumAsset) {
                if (!canUseShutterstockUnlimitedMedia) {
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.FEATURE_LOCK,
                            modal_context: {
                                featureName: LOCKED_FEATURES.UNLIMITED_PREMIUM_MEDIA,
                            },
                        }),
                    );
                    return;
                }
                handleTransformImage(aiMediaOption);
            } else if (isMarketplaceAsset) {
                if (unusedCreditCount < 1) {
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.FEATURE_LOCK,
                            modal_context: {
                                featureName: LOCKED_FEATURES.HIT_IMAGE_TRANSFORM_MEDIA_CREDIT_LIMIT,
                            },
                        }),
                    );
                    return;
                }

                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.GENERIC_ERROR,
                        modal_context: {
                            size: MODAL_SIZES.SM,
                            title: 'Heads up!',
                            content: (
                                <>
                                    Transforming this stock media will{' '}
                                    <b>use one of your marketplace credits</b>. Do you want to
                                    continue?
                                </>
                            ),
                            buttons: [
                                {
                                    component: SecondaryButton,
                                    text: 'Cancel',
                                    onClick: () => dispatch(hideModal()),
                                },
                                {
                                    component: Button,
                                    text: 'Yes, use media credit',
                                    onClick: () => {
                                        dispatch(hideModal());
                                        handleTransformImage(aiMediaOption);
                                    },
                                },
                            ],
                            closeable: true,
                        },
                    }),
                );
            } else {
                handleTransformImage(aiMediaOption);
            }
        },
        [
            assetSource,
            canUseShutterstockUnlimitedMedia,
            dispatch,
            handleTransformImage,
            transformImageData,
            unusedCreditCount,
        ],
    );

    return { onTransformImage };
};

export type TransformVideoData = {
    videoUrl: string;
    uuid: string;
    name: string;
    durationMs: number;
};

// Upstream providers cap the input reference video for video-to-video generation
// (e.g. Fal caps Kling O3 Pro Reference at 10.05s). If we add a model with a different
// cap, this constant will need to be revisited.
export const MAX_VIDEO_TO_VIDEO_INPUT_DURATION_MS = 10_000;
export const VIDEO_TO_VIDEO_INPUT_TOO_LONG_MESSAGE = `AI video editing isn't available for videos longer than ${MAX_VIDEO_TO_VIDEO_INPUT_DURATION_MS / 1000} seconds.`;

type UseTransformVideoParams = {
    transformVideoData: TransformVideoData | null;
    assetSource: SourceEnum;
    analyticsMeta?: TransformImageAnalyticsMeta;
};

export const useTransformVideo = ({
    transformVideoData,
    assetSource,
    analyticsMeta,
}: UseTransformVideoParams) => {
    const dispatch = useAppDispatch();
    const canUseShutterstockUnlimitedMedia = useSelector(getCanUseShutterstockUnlimitedMedia);
    const unusedCreditCount = useSelector(getUnusedCreditCount);

    const handleTransformVideo = useCallback(() => {
        if (!transformVideoData) return;

        const uploadedFile: UploadedFile = {
            id: transformVideoData.uuid,
            uuid: transformVideoData.uuid,
            name: transformVideoData.name,
            type: SupportedFileType.MP4,
            isUploading: false,
            local_preview_url: transformVideoData.videoUrl,
        };

        const source = getEnumKeyByEnumValue(SourceEnum, assetSource);
        dispatch(setAdvancedMediaControlsState({ isVisible: false, sceneId: '', assetId: '' }));
        dispatch(changeActiveAIMediaSubTab(AI_MEDIA_SUB_TAB_OPTIONS.VIDEO));
        dispatch(
            setSelectedReferenceVideo({
                file: uploadedFile,
                durationMs: transformVideoData.durationMs,
            }),
        );
        dispatch(switchEditorTab(EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA));
        if (analyticsMeta) {
            analytics.track(analyticsMeta.eventName, {
                location: analyticsMeta.location,
                original_media_source: source,
                action: 'edit_video',
                ...analyticsMeta.extraProps,
            });
        }
    }, [analyticsMeta, assetSource, dispatch, transformVideoData]);

    const onTransformVideo = useCallback(() => {
        if (!transformVideoData) return;

        if (
            [SourceEnum.SOURCE_DAM, SourceEnum.SOURCE_SHUTTERSTOCK_EDITORIAL].includes(assetSource)
        ) {
            ErrorReporter.capture(`User tried to transform DAM or editorial media: ${assetSource}`);
            return;
        }

        const isPremiumAsset = assetSource === SourceEnum.SOURCE_SHUTTERSTOCK_CHOICES;
        const isMarketplaceAsset = assetSource === SourceEnum.SOURCE_SHUTTERSTOCK_PREMIUM;
        if (isPremiumAsset) {
            if (!canUseShutterstockUnlimitedMedia) {
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.UNLIMITED_PREMIUM_MEDIA,
                        },
                    }),
                );
                return;
            }
            handleTransformVideo();
        } else if (isMarketplaceAsset) {
            if (unusedCreditCount < 1) {
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.HIT_IMAGE_TRANSFORM_MEDIA_CREDIT_LIMIT,
                        },
                    }),
                );
                return;
            }

            dispatch(
                showModal({
                    modal_id: MODAL_IDS.GENERIC_ERROR,
                    modal_context: {
                        size: MODAL_SIZES.SM,
                        title: 'Heads up!',
                        content: (
                            <>
                                Transforming this stock media will{' '}
                                <b>use one of your marketplace credits</b>. Do you want to continue?
                            </>
                        ),
                        buttons: [
                            {
                                component: SecondaryButton,
                                text: 'Cancel',
                                onClick: () => dispatch(hideModal()),
                            },
                            {
                                component: Button,
                                text: 'Yes, use media credit',
                                onClick: () => {
                                    dispatch(hideModal());
                                    handleTransformVideo();
                                },
                            },
                        ],
                        closeable: true,
                    },
                }),
            );
        } else {
            handleTransformVideo();
        }
    }, [
        assetSource,
        canUseShutterstockUnlimitedMedia,
        dispatch,
        handleTransformVideo,
        transformVideoData,
        unusedCreditCount,
    ]);

    return { onTransformVideo };
};
